// components/admin/ImpersonationBanner.tsx
// Fixed compact top banner (28px) shown when an admin is viewing the app as another user.
// Displays: eye icon + user name + write-unlock toggle + exit button.
// Locked by default (read-only). Unlocking turns the indicator red as a caution signal.
// Updated Jun 2026: simplified to a fixed 28px strip (no collapsible mode).

import { useNavigate } from 'react-router-dom';
import { useImpersonation, BANNER_HEIGHT } from '../../contexts/ImpersonationContext';
import { Lock, Unlock, Eye, X } from 'lucide-react';

export { BANNER_HEIGHT };

export default function ImpersonationBanner() {
  const navigate = useNavigate();
  const { impersonatedUser, isWriteUnlocked, stopImpersonation, toggleWriteUnlock } = useImpersonation();

  if (!impersonatedUser) return null;

  const handleExit = () => {
    stopImpersonation();
    navigate('/admin/users');
  };

  return (
    <div
      className="fixed top-0 left-0 right-0 flex items-center justify-between px-4 gap-3"
      style={{ height: BANNER_HEIGHT, zIndex: 9998, backgroundColor: '#78350f', color: '#fef3c7' }}
    >
      {/* Left: identity */}
      <div className="flex items-center gap-1.5 min-w-0">
        <Eye className="w-3 h-3 flex-shrink-0 text-amber-300" />
        <span className="text-xs truncate">
          Vue en tant que{' '}
          <span className="font-semibold text-white">{impersonatedUser.displayName}</span>
          <span className="hidden sm:inline text-amber-300/80"> · {impersonatedUser.email}</span>
        </span>
      </div>

      {/* Right: controls */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {/* Write-unlock toggle */}
        <button
          onClick={toggleWriteUnlock}
          aria-pressed={isWriteUnlocked}
          className={`
            flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold transition-all
            ${isWriteUnlocked
              ? 'bg-red-600/80 hover:bg-red-600 text-white border border-red-500'
              : 'bg-amber-800/60 hover:bg-amber-800 text-amber-200 border border-amber-700'
            }
          `}
          title={isWriteUnlocked ? 'Verrouiller (retour en lecture seule)' : 'Déverrouiller les actions'}
        >
          {isWriteUnlocked ? (
            <>
              <Unlock className="w-2.5 h-2.5" />
              <span className="hidden sm:inline">Écriture</span>
            </>
          ) : (
            <>
              <Lock className="w-2.5 h-2.5" />
              <span className="hidden sm:inline">Lecture</span>
            </>
          )}
        </button>

        {/* Exit */}
        <button
          onClick={handleExit}
          className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-amber-800/60 hover:bg-amber-800 text-amber-200 border border-amber-700 transition-all"
          title="Quitter le mode vue utilisateur"
        >
          <X className="w-2.5 h-2.5" />
          <span>Quitter</span>
        </button>
      </div>
    </div>
  );
}
